import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";

import { getContainerRecords } from "api/purchase";
import { Button, InputContainer, TextInput } from "components/Form";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import CreateModal from "./ContainerCreate";
import ContainerDrawer from "./ContainerDrawer";
import ReceiveModal from "./ReceiveModal";

import { CheckCircle, Download2, ExportIcon, Plus, QrCode2, Receipt } from "icons";

import { initialAPIListValue } from "utils/constants";
import { convertMergedFiltersToQueryString } from "utils/filter";
import getClearAdornment from "utils/form";

import useStyles from "../containers/styles";
import { exportToExcel } from "utils/exportToExcel";
import { convertDataToTableView } from "utils/convert";

const exportHeaders = [
  { id: "container_number" },
  { id: "po_type", label: "PO Type" },
  { id: "po_number", label: "PO Number" },
  { id: "invoice_number" },
  { id: "manufacturer" },
  { id: "vendor" },
  { id: "customer" },
  { id: "importer" },
  {
    id: "collections",
    render: (r) => (r.collections ? JSON.parse(r.collections).join(", ") : ""),
  },
  { id: "scheduled_date" },
  { id: "etd", label: "ETD" },
  { id: "eta", label: "ETA" },
  { id: "ata", label: "ATA" },
  { id: "bol_number", label: "BOL Number" },
  { id: "line" },
  { id: "vessel" },
  {
    id: "port_and_forwarder",
    label: "Port / Forwarder",
  },
  { id: "ship_to_port", label: "Port Arrival" },
  { id: "final_destination", label: "Destination" },
  { id: "hc40_freight_cost", label: "40HC Freight Cost" },
  { id: "inland_freight_cost", label: "Inland Freight Cost" },
  { id: "loading_method" },
  { id: "notes" },
];

export default function SellerContainers() {
  const [t] = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [reveiveModal, setReceiveModal] = useState(false);

  const initialCells = [
    {
      id: "container_number",
      label: "Container",
      render: (r) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{r.container_number}</span>
          {r.is_released && (
            <Tooltip arrow title="Released">
              <span height={24}>
                <CheckCircle color="green" />
              </span>
            </Tooltip>
          )}
        </div>
      ),
      sortable: true,
      flex: 1,
    },
    {
      id: "po_number",
      label: "PO",
      render: (r) => r.po_number,
      sortable: true,
      flex: 1,
    },

    {
      id: "bol_number",
      label: "BOL",
      render: (r) => r.bol_number,
      sortable: true,
      flex: 1,
    },
    {
      id: "eta",
      label: "ETA",
      render: (r) => r.eta,
      sortable: true,
      flex: 1,
    },
    {
      id: "ata",
      label: "ATA",
      render: (r) => r.ata,
      sortable: true,
      flex: 1,
    },
    {
      id: "actions",
      label: "Actions",
      render: (r) =>
        !r.is_received ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                setReceiveModal(r.id);
              }}
            >
              Receive
            </Button>
          </div>
        ) : null,
      flex: 1,
    },
  ];

  const [cells] = useState(initialCells);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState({
    po_number: "",
    container_number: "",
    bol_number: "",
    seller_id: 2,
    sku: "",
  });

  const [selectedFilters, setSelectedFilters] = useState({
    vendor: [],
    po_type: [],
    customer: [],
    importer: [],
    ship_to_port: [],
    final_destination: [],
    line: [],
  });
  const [containers, setContainers] = useState(initialAPIListValue);
  const [headerCounts, setHeaderCounts] = useState({});
  const [createModal, setCreateModal] = useState(false);
  const [container, setContainer] = useState({});
  const [editContainer, setEditContainer] = useState(null);
  const [exportDisabled, setExportDisabled] = useState(false);
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("eta");
  const history = useHistory();

  useEffect(() => {
    if (containers.isFetching) {
      let params = {
        ...search,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getContainerRecords({
        params,
        mergedFilters: convertMergedFiltersToQueryString(selectedFilters),
        setFunc: (res) => {
          setContainers(res);
          setHeaderCounts(res.customCounts);
        },
        paginated: true,
      });
    }
  }, [
    search,
    page,
    rowsPerPage,
    selectedFilters,
    containers.isFetching,
    orderDirection,
    orderBy,
  ]);

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setContainers((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setContainers((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setContainers({ ...containers, isFetching: true });
    }, 500),
    [],
  );

  const handleDrawerClose = (status) => {
    if (status) {
      setContainers((prev) => ({ ...prev, isFetching: true }));
    }
    setContainer({});
  };

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `Containers-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, exportHeaders),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    getContainerRecords({
      params: {
        limit: containers.count,
        ...search,
      },
      mergedFilters: convertMergedFiltersToQueryString(selectedFilters),
      setFunc: ({ items: { results } }) => exportData(results),
    });
  };

  return (
    <>
      <PageTitle
        title={
          <>
            {"Containers "}
            <IconButton
              onClick={() =>
                exportToExcel(
                  [
                    {
                      SKU: "",
                      UPC: "",
                      Length: "",
                      Width: "",
                      Height: "",
                      Weight: "",
                      Quantity: "",
                    },
                  ],
                  "container_product_upload_template",
                )
              }
            >
              <Download2 color="#FFF" />
            </IconButton>
          </>
        }
        multipleButtons={true}
        button={
          <>
            <Button
              variant="contained"
              size="medium"
              color="primary-light"
              onClick={() => {
                history.push("/app/seller/container-sku-list");
              }}
            >
              <span style={{ whiteSpace: "nowrap" }}>SKU List</span>
            </Button>
            <Button
              endIcon={<Plus color="white" />}
              variant="contained"
              size="medium"
              color="primary-light"
              onClick={() => setCreateModal(true)}
            >
              Create
            </Button>
            <Button
              endIcon={<ExportIcon color="white" />}
              variant="contained"
              size="medium"
              color="primary-light"
              onClick={() => handleExportData()}
            >
              Export
            </Button>
          </>
        }
      />
      <InputContainer>
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="container_number"
              label={t("container_number")}
              variant="outlined"
              fullWidth
              value={search.container_number}
              onChange={handleChangeFilterInput("container_number")}
              InputProps={clearAdornment("container_number")}
              LeftIcon={Receipt}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="po_number"
              label={t("po_number")}
              variant="outlined"
              fullWidth
              value={search.po_number}
              onChange={handleChangeFilterInput("po_number")}
              InputProps={clearAdornment("po_number")}
              LeftIcon={Receipt}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="bol_number"
              label={t("bol_number")}
              variant="outlined"
              fullWidth
              value={search.bol_number}
              onChange={handleChangeFilterInput("bol_number")}
              InputProps={clearAdornment("bol_number")}
              LeftIcon={Receipt}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextInput
              id="sku"
              label={t("SKU")}
              variant="outlined"
              fullWidth
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            data={containers}
            headers={cells}
            headerCounts={headerCounts}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            filters={search}
            onFilter={() => {
              setContainers({ ...containers, isFetching: true });
            }}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            rowOnClick={(r) => setContainer(r)}
            highlightColor={theme.palette.primary.main}
            className={classes.table}
            highlightStyle={(_r) => ({
              backgroundColor: "white",
              borderBottom: "1px solid #dddddd90",
              height: "4rem",
            })}
            disableExportData={true}
            exportDisabled={exportDisabled}
            handleExportData={handleExportData}
            sortActions={{ onSort, orderBy, orderDirection }}
          />
          {Object.keys(container).length ? (
            <ContainerDrawer
              onClose={handleDrawerClose}
              open={true}
              container={container}
              onEdit={(container) => {
                setEditContainer(container);
                handleDrawerClose();
              }}
              onFileDelete={() => {
                setContainers({ ...containers, isFetching: true });
                handleDrawerClose();
              }}
            />
          ) : null}
        </Grid>
      </Grid>
      <CreateModal
        open={createModal || editContainer ? true : false}
        editContainer={editContainer}
        onClose={() => {
          setCreateModal(false);
          setEditContainer(null);
        }}
        onEdit={() => {
          setEditContainer(null);
          setContainers({ ...containers, isFetching: true });
        }}
        onCreate={() => setContainers({ ...containers, isFetching: true })}
        t={t}
      />
      <ReceiveModal
        containerId={reveiveModal}
        open={Boolean(reveiveModal)}
        onClose={(status) => {
          setReceiveModal(false);
          if (status) {
            setContainers({ ...containers, isFetching: true });
          }
        }}
      />
    </>
  );
}
